import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import {
  CalendarDays, Sparkles, Loader2, ArrowRight, Clock, User, Link2,
  ChevronRight, CheckCircle2, ListChecks, BarChart3, Zap, Target,
} from 'lucide-react';
import { roadmapApi } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const PRIORITY_CFG = {
  urgente: { color: 'bg-red-600', light: 'bg-red-50 text-red-700 border-red-200', label: 'Urgente' },
  alta: { color: 'bg-orange-500', light: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Alta' },
  media: { color: 'bg-blue-500', light: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Média' },
  baixa: { color: 'bg-slate-400', light: 'bg-slate-50 text-slate-700 border-slate-200', label: 'Baixa' },
};

const ROLE_CFG = {
  ciso: 'CISO',
  ai_officer: 'AI Officer',
  dpo: 'DPO',
  it_team: 'Equipa IT',
  legal: 'Jurídico',
};

// markdown bold
function md(s) {
  if (!s) return null;
  const parts = []; let last = 0; let idx = 0;
  const re = /\*\*([^*]+)\*\*/g; let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    parts.push(<strong key={idx++} className="font-semibold">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
}

export const RoadmapDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [focus, setFocus] = useState('');
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [appliedResult, setAppliedResult] = useState(null);

  const reset = () => { setRoadmap(null); setFocus(''); setAppliedResult(null); };

  const handleGenerate = async () => {
    setGenerating(true);
    setAppliedResult(null);
    try {
      const res = await roadmapApi.generate({ horizon_days: 90, focus: focus.trim() || null });
      setRoadmap(res.data);
      toast.success(`Plano gerado: ${res.data.total_tasks} tarefas em ${res.data.weeks.length} semanas`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao gerar plano (~20s)');
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = async () => {
    if (!roadmap) return;
    setApplying(true);
    try {
      const res = await roadmapApi.apply(roadmap.roadmap_id);
      setAppliedResult(res.data);
      toast.success(`${res.data.tasks_created} tarefas criadas no módulo NIS2 Tasks`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aplicar plano');
    } finally {
      setApplying(false);
    }
  };

  const handleClose = (val) => {
    if (!val) reset();
    onOpenChange(val);
  };

  const stats = useMemo(() => {
    if (!roadmap) return null;
    const allTasks = roadmap.weeks.flatMap(w => w.tasks);
    const byPriority = { urgente: 0, alta: 0, media: 0, baixa: 0 };
    let totalHours = 0;
    allTasks.forEach(t => {
      byPriority[t.priority] = (byPriority[t.priority] || 0) + 1;
      totalHours += t.duration_hours || 0;
    });
    return { byPriority, totalHours, totalWeeks: roadmap.weeks.length };
  }, [roadmap]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="roadmap-dialog"
      >
        <DialogHeader className="p-6 pb-4 bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center backdrop-blur-sm">
              <CalendarDays className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                Plano de Ação 90 Dias
                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/25 text-[10px]">
                  <Sparkles className="h-2.5 w-2.5 mr-1" />Motor de IA
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-white/90 mt-1">
                Roadmap personalizado de conformidade EU AI Act, baseado no estado real do tenant.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {!roadmap && (
            <div className="p-8 space-y-6" data-testid="roadmap-empty-state">
              <div className="text-center max-w-xl mx-auto space-y-3">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto">
                  <Target className="h-7 w-7 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">Gera o teu Plano de Ação</h3>
                <p className="text-sm text-slate-600">
                  O Motor de IA vai analisar todos os teus sistemas IA, documentação, riscos, incidentes e auditorias para criar um <strong>roadmap semanal</strong> com tarefas concretas, dependências e responsáveis sugeridos.
                </p>
              </div>

              <div className="max-w-xl mx-auto space-y-2">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-widest">
                  Foco opcional (área prioritária)
                </label>
                <Textarea
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                  placeholder='Ex: "Preparação para auditoria externa", "Lançamento de novo modelo GPAI", "Recuperar atraso na Doc Técnica"...'
                  className="resize-none"
                  rows={3}
                  data-testid="roadmap-focus-input"
                />
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  size="lg"
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-md"
                  data-testid="roadmap-generate-btn"
                >
                  {generating ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar plano (~20s)...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 mr-2" />Gerar Plano com IA</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {roadmap && (
            <>
              {/* Summary header */}
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1" data-testid="roadmap-title">{roadmap.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{md(roadmap.summary)}</p>
                  </div>
                  {stats && (
                    <div className="flex items-center gap-3 text-xs">
                      <div className="px-3 py-2 bg-white rounded-lg border border-slate-200 text-center">
                        <div className="text-lg font-bold text-indigo-600 tabular-nums">{roadmap.total_tasks}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Tarefas</div>
                      </div>
                      <div className="px-3 py-2 bg-white rounded-lg border border-slate-200 text-center">
                        <div className="text-lg font-bold text-purple-600 tabular-nums">{stats.totalWeeks}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Semanas</div>
                      </div>
                      <div className="px-3 py-2 bg-white rounded-lg border border-slate-200 text-center">
                        <div className="text-lg font-bold text-slate-700 tabular-nums">{stats.totalHours}h</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">Estimado</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tabs Timeline / Gantt */}
              <Tabs defaultValue="timeline" className="flex-1 flex flex-col overflow-hidden">
                <div className="px-6 pt-3 border-b border-slate-200">
                  <TabsList className="bg-slate-100">
                    <TabsTrigger value="timeline" data-testid="tab-timeline">
                      <ListChecks className="h-4 w-4 mr-1.5" />Timeline
                    </TabsTrigger>
                    <TabsTrigger value="gantt" data-testid="tab-gantt">
                      <BarChart3 className="h-4 w-4 mr-1.5" />Gantt
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Timeline view */}
                <TabsContent value="timeline" className="flex-1 overflow-hidden m-0">
                  <ScrollArea className="h-[55vh]">
                    <div className="p-6 space-y-6" data-testid="roadmap-timeline-view">
                      {roadmap.weeks.map((w) => (
                        <TimelineWeek key={w.week_number} week={w} navigate={navigate} />
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                {/* Gantt view */}
                <TabsContent value="gantt" className="flex-1 overflow-hidden m-0">
                  <ScrollArea className="h-[55vh]">
                    <GanttView weeks={roadmap.weeks} />
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>

        {/* Footer */}
        {roadmap && (
          <DialogFooter className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex !flex-row !justify-between items-center gap-3">
            {appliedResult ? (
              <div className="flex items-center gap-3 text-sm" data-testid="roadmap-applied-success">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                <span className="text-emerald-700 font-medium">
                  {appliedResult.tasks_created} tarefas criadas em NIS2 Tasks
                </span>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => { handleClose(false); navigate('/tasks'); }}
                  className="text-indigo-600 hover:text-indigo-700 px-2"
                  data-testid="roadmap-goto-tasks"
                >
                  Ver tarefas <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Zap className="h-3.5 w-3.5" />
                Preview — as tarefas só serão criadas após confirmação
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => reset()}
                disabled={generating || applying}
                data-testid="roadmap-regenerate-btn"
              >
                <Sparkles className="h-4 w-4 mr-1.5" />Gerar outro
              </Button>
              {!appliedResult && (
                <Button
                  onClick={handleApply}
                  disabled={applying}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white"
                  data-testid="roadmap-apply-btn"
                >
                  {applying ? (
                    <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />A criar tarefas...</>
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 mr-1.5" />Confirmar e criar tarefas</>
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Timeline (vertical) view ----------
const TimelineWeek = ({ week, navigate }) => {
  return (
    <div className="relative pl-8 border-l-2 border-indigo-200" data-testid={`timeline-week-${week.week_number}`}>
      <div className="absolute -left-[14px] top-0 h-7 w-7 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center text-xs font-bold shadow-md">
        W{week.week_number}
      </div>
      <div className="mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold text-slate-900">Semana {week.week_number}</h4>
          <Badge variant="outline" className="text-[10px] bg-white">
            <CalendarDays className="h-2.5 w-2.5 mr-1" />
            {week.start_date} → {week.end_date}
          </Badge>
        </div>
        {week.focus && <p className="text-sm text-slate-600 italic mt-1">{week.focus}</p>}
      </div>
      <div className="space-y-2">
        {week.tasks.map((t, i) => {
          const pCfg = PRIORITY_CFG[t.priority] || PRIORITY_CFG.media;
          return (
            <div
              key={i}
              className="bg-white border border-slate-200 hover:border-indigo-300 rounded-lg p-3 transition-colors"
              data-testid={`timeline-task-${week.week_number}-${i}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className={cn('text-[10px]', pCfg.light)}>{pCfg.label}</Badge>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />{t.duration_hours}h
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <User className="h-3 w-3" />{ROLE_CFG[t.suggested_role] || t.suggested_role}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-slate-900 mb-1">{t.title}</p>
                  {t.description && <p className="text-xs text-slate-600 leading-relaxed">{t.description}</p>}
                  {t.depends_on && t.depends_on.length > 0 && (
                    <div className="mt-2 flex items-start gap-1 text-[11px] text-amber-700">
                      <Link2 className="h-3 w-3 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">Depende de: <em>{t.depends_on.join(', ')}</em></span>
                    </div>
                  )}
                </div>
                {t.href && (
                  <button
                    onClick={() => navigate(t.href)}
                    className="text-slate-400 hover:text-indigo-600 shrink-0 mt-0.5"
                    title="Abrir módulo"
                    data-testid={`timeline-task-href-${week.week_number}-${i}`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Gantt (horizontal bar) view ----------
const GanttView = ({ weeks }) => {
  // Flatten all tasks with their week numbers
  const rows = useMemo(() => {
    const r = [];
    weeks.forEach(w => {
      w.tasks.forEach(t => {
        r.push({
          week: w.week_number,
          title: t.title,
          priority: t.priority,
          duration_hours: t.duration_hours,
          suggested_role: t.suggested_role,
        });
      });
    });
    return r;
  }, [weeks]);

  const maxWeek = Math.max(...weeks.map(w => w.week_number), 1);
  const weekCols = Array.from({ length: maxWeek }, (_, i) => i + 1);
  const COL_W = 80; // px per week

  return (
    <div className="p-6" data-testid="roadmap-gantt-view">
      <div className="overflow-x-auto">
        <div style={{ minWidth: `${300 + maxWeek * COL_W}px` }}>
          {/* Header */}
          <div className="flex border-b-2 border-slate-200 sticky top-0 bg-white z-10">
            <div className="w-[300px] shrink-0 px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-widest">
              Tarefa
            </div>
            <div className="flex flex-1">
              {weekCols.map(w => (
                <div
                  key={w}
                  className="text-center text-xs font-semibold text-slate-600 py-2 border-l border-slate-200 bg-slate-50"
                  style={{ width: `${COL_W}px` }}
                >
                  W{w}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          <div>
            {rows.map((row, idx) => {
              const pCfg = PRIORITY_CFG[row.priority] || PRIORITY_CFG.media;
              return (
                <div
                  key={idx}
                  className="flex border-b border-slate-100 hover:bg-slate-50 group"
                  data-testid={`gantt-row-${idx}`}
                >
                  <div className="w-[300px] shrink-0 px-3 py-2 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge variant="outline" className={cn('text-[9px] py-0 px-1.5', pCfg.light)}>{pCfg.label}</Badge>
                      <span className="text-[10px] text-slate-500">{row.duration_hours}h</span>
                    </div>
                    <p className="text-xs text-slate-800 line-clamp-2 leading-snug">{row.title}</p>
                  </div>
                  <div className="flex flex-1 relative">
                    {weekCols.map(w => (
                      <div
                        key={w}
                        className="border-l border-slate-100"
                        style={{ width: `${COL_W}px` }}
                      />
                    ))}
                    {/* The bar */}
                    <div
                      className={cn(
                        'absolute top-1/2 -translate-y-1/2 h-5 rounded shadow-sm flex items-center justify-center text-[10px] text-white font-medium px-2',
                        pCfg.color
                      )}
                      style={{
                        left: `${(row.week - 1) * COL_W + 4}px`,
                        width: `${COL_W - 8}px`,
                      }}
                      title={row.title}
                    >
                      W{row.week}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoadmapDialog;
